/**
 * SDK 房券列表请求(同源 BFF)
 *
 * 房券列表为公开端点(无需登录),hotelId/tenant/brand 由 BFF 服务端按域名推导(SDK 不注入)。
 * 走 SDK 请求层 post(/api/vouchers/list),代理 /api/product/offical/roomCoupon/list,响应裸数组。
 *
 * 模块级缓存 + 并发去重:房券列表会话内稳定(不依赖日期),缓存命中避免 datebar 切日期重渲染时
 * 重复打 BFF(镜像 coupons.ts:fetchGroupDetail 的缓存策略)。仅 HTTP 200 缓存(含空数组也缓存,
 * 避免无房券酒店反复打);瞬时失败(网络/非 200)不缓存、返回 null(fail-closed=不渲染模块)。
 *
 * 复制 transformApiVoucherList 纯函数(来自 src/lib/api/voucher.ts),不导入 lib/api/voucher
 * 以防其后续引入重依赖污染 SDK bundle(同 packages.ts 复制 mapApiRoomToUI 策略)。
 */
import type { ApiVoucherListItem } from '@/types/api-voucher';
import type { Voucher } from '@/types/voucher';
import { post } from './client';
import { getDomain } from './hotel';

/** 复制自 src/lib/api/voucher.ts:getVoucherPriceUnit（纯函数，无运行时依赖） */
export function getVoucherPriceUnit(buyLimitType: number | undefined | null): string {
  if (buyLimitType === 1 || buyLimitType === 4) return '晚';
  if (buyLimitType === 2) return '间';
  if (buyLimitType === 6) return '间夜';
  if (buyLimitType == null) return '晚';
  return `晚${buyLimitType}`;
}

/** 复制自 src/lib/api/voucher.ts:transformApiVoucherList(纯函数,无运行时依赖) */
export function transformApiVoucherList(api: ApiVoucherListItem[]): Voucher[] {
  return api.map((item): Voucher => {
    const afterCouponPrice = item.afterCouponPrice ?? 0;
    const originalPrice = item.price ?? 0;
    const discountAmount = item.discountAmount ?? 0;
    // 有优惠金额(discountAmount)才显示券后价:以优惠金额为判定,券后价为实际到手价
    const hasCouponPrice = discountAmount > 0 && afterCouponPrice > 0;
    return {
      id: String(item.skuid ?? ''),
      hotelId: '',
      name: item.pageTitle ?? item.skuName ?? '',
      brief: item.tags,
      roomId: 0,
      nights: item.nightRoomCount ?? 1,
      buyLimitType: item.buyLimitType,
      // 券后价优先(有优惠金额时为实际到手价),否则取售价
      price: hasCouponPrice ? afterCouponPrice : originalPrice,
      afterCouponPrice: hasCouponPrice ? afterCouponPrice : undefined,
      originalPrice: hasCouponPrice ? originalPrice : undefined,
      images: item.picUrl ? [item.picUrl] : [],
      validPeriod: { endAt: item.saleEndDate },
      returnPolicy: item.returnPolicy,
      paidCount: item.paidCount,
    };
  });
}

/** 模块级缓存:domain -> 房券列表(含空数组);会话内稳定 */
const voucherCache = new Map<string, Voucher[]>();
/** 并发去重锁:同 domain 多实例/多请求共享同一次 fetch */
const inflight = new Map<string, Promise<Voucher[] | null>>();

/**
 * 拉取房券列表(hotelId 由 BFF 服务端推导,无客户端入参)。
 * @returns 房券列表;空数组也返回(调用方据 length 判是否渲染);异常/非 200 返回 null(不渲染)
 */
export async function fetchVoucherList(): Promise<Voucher[] | null> {
  const domain = getDomain();
  const cacheKey = domain || '_';

  if (voucherCache.has(cacheKey)) return voucherCache.get(cacheKey) ?? [];

  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const p = (async (): Promise<Voucher[] | null> => {
    try {
      // 请求日志:排查"券后价缺失"--记录请求 URL/body 与推导用的 domain
      console.log('[SDK voucher-list] request', { url: '/api/vouchers/list', body: {}, domain });
      const data = await post<ApiVoucherListItem[]>('/api/vouchers/list', {});
      // 返回日志:打印上游裸数组(含 afterCouponPrice/discountAmount/vipPrice 等价格字段,
      // 便于确认后端是否返回券后价 / 字段名是否一致),以及转换后的到手价决策
      console.log('[SDK voucher-list] response raw', {
        count: (data ?? []).length,
        items: data,
      });
      const list = transformApiVoucherList(data ?? []);
      console.log('[SDK voucher-list] response transformed', {
        items: list.map((v) => ({
          id: v.id,
          name: v.name,
          price: v.price,
          afterCouponPrice: v.afterCouponPrice,
          originalPrice: v.originalPrice,
        })),
      });
      voucherCache.set(cacheKey, list); // 含空数组也缓存(无房券的酒店不反复打)
      return list;
    } catch (err) {
      console.warn('[SDK voucher-list] error', err);
      return null; // 网络/非 200:fail-closed,不缓存
    } finally {
      inflight.delete(cacheKey);
    }
  })();

  inflight.set(cacheKey, p);
  return p;
}
